import React, { useEffect, useState } from 'react';
import VerticalTimer from '@ui-kit/components/verticalTimer';
import { container } from 'tsyringe';
import Avatar from './avatar';
import PersonalInfo from './personalInfo';
import { BaseGameState, IGameService, ITable } from '@base/features/game/model/interfaces';
import { t, useBehaviorSubject, useSubject } from '@base/utils/helpers';
import Button from '@ui-kit/components/button';
import DelayUnmountChild from '@ui-kit/components/delayUnmountChild';
import { ReactComponent as StarIcon } from '@ui-kit/icons/star-coin.svg';
import { ReactComponent as Empty } from '@ui-kit/icons/empty-player.svg';
import ModalWarning from '@base/features/modal/components/ModalWarning';
import Color from '@base/features/game/model/color';
import MainButton from '@ui-kit/components/mainButton';
import classNames from 'classnames';
import { IPlayer, IPlayersOnlineRepository } from '@base/features/player/model/interfaces';
import { IModalProvider } from '@base/features/modal/model/interfaces';
import LobbyService from '@base/features/lobby/model/lobbyService';
import Codes from '@base/features/socket/codes';

export default function PlayerInfo(props: {
  table: ITable;
  color: Color;
  gameState: BaseGameState;
  player: IPlayer | undefined;
  children: JSX.Element;
  icon: string;
}) {
  const player = props.player;
  const playersOnlineRepository: IPlayersOnlineRepository = container.resolve('playersOnlineRepository');
  const modalProvider: IModalProvider = container.resolve('modalProvider');
  const gameService: IGameService = container.resolve('gameService');
  const lobbyService: LobbyService = container.resolve('lobbyService');
  const gameInProgress = useBehaviorSubject(props.gameState.gameInProgress);
  const isCloseModal = useBehaviorSubject(modalProvider.isCloseModal);
  const showBan =
    !gameInProgress &&
    playersOnlineRepository.me?.pid !== player?.pid &&
    props.table.getOwner()?.pid === playersOnlineRepository.me?.pid;

  const time = useBehaviorSubject(props.gameState.clock.onClockTick);
  const players = useSubject(props.gameState.onPlayersCountChanged);
  const winner = useSubject(gameService.winnerName);

  useEffect(() => {
    if (isCloseModal) {
      gameService.winnerName.next(undefined);
      modalProvider.isCloseModal.next(false);
    }
  }, [isCloseModal, winner]);

  useEffect(() => player && playersOnlineRepository.sendFingerPid(player.pid), [
    playersOnlineRepository,
    player,
    players,
  ]);

  const handleBan = (player: IPlayer) =>
    lobbyService.sendTableOp(player.id, player.getTable()?.tid, Codes.TableOpMode.BAR);

  return (
    <div className="player-info">
      {player ? (
        <>
          <VerticalTimer
            remainingTime={time}
            isPlaying={player.isPlayingTurn()}
            duration={props.table.timePerMove}
          />
          <Avatar player={player} icon={props.icon}/>
          <div className={`player-info__card ${player.isPlayingTurn() ? 'player-info__card_active' : ''}`}>
            <WinnersBadge isMount={player.getName() === winner} />
            <PersonalInfo player={player} />
            <div className="game-info">
              {showBan ? <BanButton onBan={() => player && handleBan(player)} /> : props.children}
            </div>
          </div>
        </>
      ) : props.gameState.imWatching() ? (
        <MainButton onClick={() => lobbyService.playTable(props.table.tid)}>{t('play')}</MainButton>
      ) : (
        <>
          <VerticalTimer remainingTime={time} isPlaying={false} duration={props.table.timePerMove} />
          <div className="player-info__empty empty-avatar">
            <Empty width={70} height={70} />
          </div>
          <div className={`player-info__card empty-card`}>
            <span className="player-info__empty empty-name" />
            <div>
              <div className="player-info__empty empty-flag" />
              <div className="player-info__empty empty-rating" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const BanButton = (props: { onBan: () => void }) => {
  const [showModal, setShowModal] = useState(false);

  const banModal = {
    title: t('warning'),
    body: t('selected_ban_opponent_from_table'),
    buttons: [
      {
        title: t('yes'),
        onClick: () => {
          props.onBan();
          setShowModal(false);
        },
      },
      { title: t('no'), onClick: () => setShowModal(false) },
    ],
  };

  return (
    <>
      <Button onClick={() => setShowModal(true)} color="transparent" bordered={true}>
        {t('ban')}
      </Button>
      {showModal && <ModalWarning modal={banModal} />}
    </>
  );
};

function WinnersBadge(props: { isMount: boolean }) {
  return (
    <DelayUnmountChild isMountChild={props.isMount} delayTime={700}>
      <div className={classNames('player-winner', props.isMount ? 'player-winner_visible' : '')}>
        <div>
          <StarIcon />
          <span>WINNER</span>
        </div>
      </div>
    </DelayUnmountChild>
  );
}
